/**
 * UserChangePasswordComponent
 * 
 * Author: Prajna
 * Date: 2025-01-31
 * 
 * This component allows the Super Admin to change the password of system users.
 * It includes validation checks for:
 * 1. Password length (minimum 8 characters).
 * 2. Matching new password and confirm password fields.
 * 3. Checking if the new password is different from the current password.
 * 
 * On successful password change:
 * 1. It shows a success message via MatSnackBar.
 * 2. Resets the password fields and form controls.
 * 
 * On failure:
 * 1. Displays an appropriate error message via MatSnackBar.
 * 
 * Dependencies:
 * - MatSnackBar: To show success/error messages.
 * - ChangePasswordService: To call the API for changing passwords.
 * 
 * Password Visibility Toggle:
 * - Allows users to toggle between showing and hiding passwords.
 * 
 * This component is designed to be used in a dialog window, allowing for 
 * password change functionality while keeping the UI responsive.
 */
import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/auth.service';
import { ChangePasswordService } from 'src/app/change-password.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';


@Component({
    selector: 'app-user-change-password',
    templateUrl: './user-change-password.component.html',
    styleUrls: ['./user-change-password.component.css'],
    standalone: false
})
export class UserChangePasswordComponent implements OnInit {
 currentPassword = '';
  newPassword = '';
  confirmNewPassword = '';
  emailAddress='';
  userId = 0;
  userName='';
  isLoading = false;
  message = '';
  isSuccess = false;
  showPassword: boolean = false; // Password visibility toggle
  // Define the minimum password length
  private readonly MIN_PASSWORD_LENGTH = 8;
 


  // Validate if new password and confirm new password match
  get passwordsMatch(): boolean {
    return this.newPassword === this.confirmNewPassword;
  }

  // Validate if the new password meets the minimum length
  get isPasswordValid(): boolean {
    return this.newPassword.length >= this.MIN_PASSWORD_LENGTH;
  }

  constructor(
     @Inject(MAT_DIALOG_DATA) public data: any,
    private changePasswordService: ChangePasswordService,
    private authService: AuthService,
    private snackBar: MatSnackBar,// Inject MatSnackBar service
    private dialogRef: MatDialogRef<UserChangePasswordComponent>
  ) {
    this.userName=data.userName;
    this.userId = data.userID;
  }

  ngOnInit(): void {
    const user = this.authService.getUserDetails();
    //if (user && user.userId) {
    //  this.userId = Number(user.userId); // Convert string to number
      this.emailAddress=user.emailAddress;
    //}


  }

  async onSubmit(passwordForm: NgForm) {
    // Check if passwords match
    if (!this.passwordsMatch) {
      this.message = 'New Password and Confirm New Password do not match!';
      this.isSuccess = false;
      this.showSnackBar(this.message, 'error'); // Show snackbar on failure
      return;
    }
  
    // Check if password meets minimum length
    if (!this.isPasswordValid) {
      this.message = `Password must be at least ${this.MIN_PASSWORD_LENGTH} characters long.`;
      this.isSuccess = false;
      this.showSnackBar(this.message, 'error'); // Show snackbar on failure
      return;
    }
  
    // Check if new password is the same as the old password
    if (this.currentPassword === this.newPassword) {
      this.message = 'New password cannot be the same as the current password!';
      this.isSuccess = false;
      this.showSnackBar(this.message, 'error'); // Show snackbar on failure
      return;
    }
  
    // Show loading indicator
    this.isLoading = true;
    this.message = '';
  
    try {
      // Call the change password service asynchronously
      const response = await this.changePasswordService.changePasswordBySuperAdmin(
        this.userId,
        this.emailAddress,
        this.newPassword
      );
  
      // Handle success
      this.isSuccess = true;
      this.message = response.message || 'Password changed successfully!';
      this.showSnackBar(this.message, 'success'); // Show snackbar on success
      // Clear the fields after successful change
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmNewPassword = '';
      this.dialogRef.close();
  
      // Mark form controls as untouched after clearing the fields
      passwordForm.resetForm();
    } catch (error) {
      // Handle error
      this.isSuccess = false;
      this.message =
        error.error?.message || 'An error occurred while changing the password.';
      this.showSnackBar(this.message, 'error'); // Show snackbar on failure
    } finally {
      // Hide loading indicator
      this.isLoading = false;
    }
  }
  

  // Method to display the snackbar message
  // private showSnackBar(message: string, type: 'success' | 'error'): void {
  //   const snackBarClass = type === 'success' ? 'snack-success' : 'snack-error';
  //   this.snackBar.open(message, 'Close', {
  //     duration: 5000, // Duration in milliseconds
  //     panelClass: [snackBarClass], // Apply custom class based on message type
  //   });
  // }
private showSnackBar(message: string, type: 'success' | 'error'): void {
  this.snackBar.open(message, 'Close', {
    duration: 5000,
    panelClass: type === 'success' ? ['snack-success-box'] : ['snack-error-box'],
  });
}
  

   // Method to toggle password visibility
 togglePasswordVisibility() {
  this.showPassword = !this.showPassword;
}
  /**
   * Cancels the dialog and closes it.
   */
  onCancel(): void {
    this.dialogRef.close();
  }

}
